import apiService from './apiService';

/**
 * @typedef {{ lat: number, lng: number }} BoundaryPoint
 *
 * @typedef {{
 *   id       : number,
 *   lat      : number,
 *   lng      : number,
 *   category : string,
 *   label    : string | null,
 *   notes    : string | null
 * }} Feature
 *
 * PropertyListItem — returned by GET /properties (no features).
 * @typedef {{
 *   id                         : number,
 *   name                       : string,
 *   description                : string | null,
 *   notes                      : string | null,
 *   is_livestock_staging_area  : boolean,
 *   livestock_staging_notes    : string | null,
 *   created_at                 : string,
 *   boundary                   : BoundaryPoint[]
 * }} PropertyListItem
 *
 * Property — returned by GET /properties/{id} (includes features).
 * @typedef {PropertyListItem & { features: Feature[] }} Property
 *
 * StagingProperty — returned by GET /properties/staging.
 * @typedef {{
 *   id                         : number,
 *   name                       : string,
 *   livestock_staging_notes    : string | null,
 *   owner_name                 : string | null,
 *   owner_phone                : string | null,
 *   owner_email                : string | null,
 *   distance_miles             : number
 * }} StagingProperty
 *
 * MutationResult — returned by POST, PUT, DELETE.
 * @typedef {{ id: number, message: string }} MutationResult
 */

class PropertiesService {
  /**
   * List all properties belonging to the authenticated user.
   * Features are NOT included — call getProperty(id) to get them.
   *
   * GET /properties
   *
   * Success response (HTTP 200): PropertyListItem[]
   * Error responses:
   *   HTTP 401 – { error: '...' }
   *   HTTP 503 – { error: '...' }
   */
  async getProperties() {
    try {
      return await apiService.get('/properties');
      
    } catch (error) {
      console.warn('getProperties API unavailable — using mock data:', error.message);
      return this.getMockPropertyList();
    }
  }

  /**
   * Fetch a single property including its feature pins.
   *
   * GET /properties/{id}
   *
   * Success response (HTTP 200): Property (with features array)
   * Error responses:
   *   HTTP 401 – { error: '...' }
   *   HTTP 404 – { error: '...' }
   *   HTTP 503 – { error: '...' }
   */
  async getProperty(id) {
    try {
      return await apiService.get(`/properties/${id}`);
    } catch (error) {
      console.warn('getProperty API unavailable — using mock data:', error.message);
      return this.getMockProperty(id);
    }
  }

  /**
   * Find nearby properties flagged as livestock staging areas.
   *
   * GET /properties/staging?lat={lat}&lng={lng}&radius_miles={radius_miles}&exclude_property_id={id}
   *
   * @param {{ lat: number, lng: number }} point
   * @param {number} [radiusMiles=50]
   * @param {number|null} [excludePropertyId=null]
   * @returns {Promise<StagingProperty[]>}
   */
  async getStagingProperties({ lat, lng }, radiusMiles = 50, excludePropertyId = null) {
    try {
      let url = `/properties/staging?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}&radius_miles=${encodeURIComponent(radiusMiles)}`;
      if (excludePropertyId != null) {
        url += `&exclude_property_id=${encodeURIComponent(excludePropertyId)}`;
      }
      return await apiService.get(url);
    } catch (error) {
      console.warn('getStagingProperties API unavailable:', error.message);
      return [];
    }
  }

  /**
   * Create a new property for the authenticated user.
   * The server assigns the id and returns it in MutationResult.
   *
   * POST /properties
   *
   * Request body (PropertyInput):
   *   { name*, boundary* (≥3 pts), description?, notes?, features? }
   *   Feature ids are stripped — the server assigns its own.
   *
   * Success response (HTTP 201): MutationResult  { id, message }
   * Error responses:
   *   HTTP 400 – { error: '...' }   validation failure
   *   HTTP 401 – { error: '...' }
   *   HTTP 500 – { error: '...' }
   *   HTTP 503 – { error: '...' }
   */
  async createProperty(propertyData) {
    try {
      return await apiService.post('/properties', this._buildInput(propertyData));
    } catch (error) {
      console.warn('createProperty API unavailable — using mock flow:', error.message);
      return { mock: true, id: Date.now(), message: 'Property created (mock)' };
    }
  }

  /**
   * Wholesale-replace an existing property and its features.
   * Send the complete features array you want the property to end up with;
   * an empty array deletes all features.
   *
   * PUT /properties/{id}
   *
   * Request body (PropertyInput): same shape as createProperty.
   *
   * Success response (HTTP 200): MutationResult  { id, message }
   * Error responses:
   *   HTTP 400 – { error: '...' }
   *   HTTP 401 – { error: '...' }
   *   HTTP 404 – { error: '...' }
   *   HTTP 500 – { error: '...' }
   *   HTTP 503 – { error: '...' }
   */
  async updateProperty(id, propertyData) {
    try {
      return await apiService.put(`/properties/${id}`, this._buildInput(propertyData));
    } catch (error) {
      console.warn('updateProperty API unavailable — using mock flow:', error.message);
      return { mock: true, id, message: 'Property updated (mock)' };
    }
  }

  /**
   * Delete a property. Cascades to its feature pins (FK ON DELETE CASCADE).
   *
   * DELETE /properties/{id}
   *
   * Success response (HTTP 200): MutationResult  { id, message }
   * Error responses:
   *   HTTP 401 – { error: '...' }
   *   HTTP 404 – { error: '...' }
   *   HTTP 500 – { error: '...' }
   *   HTTP 503 – { error: '...' }
   */
  async deleteProperty(id) {
    try {
      return await apiService.delete(`/properties/${id}`);
    } catch (error) {
      console.warn('deleteProperty API unavailable — using mock flow:', error.message);
      return { mock: true, id, message: 'Property deleted (mock)' };
    }
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Build a PropertyInput payload from local property state.
   * - Strips client-generated feature ids (server assigns its own).
   * - Defaults optional nullable fields to null rather than empty string.
   */
  _buildInput(propertyData) {
    return {
      name:                      propertyData.name,
      description:               propertyData.description || null,
      notes:                     propertyData.notes       || null,
      is_livestock_staging_area: !!propertyData.is_livestock_staging_area,
      livestock_staging_notes:   propertyData.livestock_staging_notes || null,
      boundary:                  propertyData.boundary    ?? [],
      features: (propertyData.features ?? []).map(({ id: _id, ...rest }) => rest),
    };
  }

  // ---------------------------------------------------------------------------
  // Mock data
  // ---------------------------------------------------------------------------

  /** PropertyListItem[] — no features, mirrors GET /properties response. */
  getMockPropertyList() {
    return [] ;
    /*
      {
        id: 1,
        name: 'Bar-HH Ranch',
        description: 'Cow/calf operation running ~300 head of Angus-cross cattle.',
        notes: 'Call ahead before accessing south pasture — working dogs on site.',
        created_at: '2025-01-10 08:00:00+00',
        boundary: [
          { lat: 41.482, lng: -99.812 },
          { lat: 41.482, lng: -99.770 },
          { lat: 41.465, lng: -99.770 },
          { lat: 41.465, lng: -99.792 },
          { lat: 41.455, lng: -99.792 },
          { lat: 41.455, lng: -99.812 },
        ],
      },
      {
        id: 2,
        name: 'Prairie View Farm',
        description: '1,800-acre dryland wheat and corn operation with a small cow-calf herd.',
        notes: 'Locked front gate — contact owner for access code.',
        created_at: '2025-02-14 14:22:00+00',
        boundary: [
          { lat: 43.082, lng: -107.300 },
          { lat: 43.082, lng: -107.265 },
          { lat: 43.065, lng: -107.265 },
          { lat: 43.065, lng: -107.300 },
        ],
      },
    ];
    */
  }

  /** Property — with features, mirrors GET /properties/{id} response. */
  getMockProperty(id) {
    const features = {
      1: [
        { id: 1, lat: 41.475, lng: -99.800, category: 'gate',      label: 'Main Gate',    notes: 'Gate code: 4821' },
        { id: 2, lat: 41.462, lng: -99.785, category: 'water',     label: 'North Pond',   notes: 'Seasonal — may be dry July–Sept' },
        { id: 3, lat: 41.470, lng: -99.810, category: 'livestock', label: 'Calving Barn', notes: null },
      ],
      2: [
        { id: 4, lat: 43.078, lng: -107.295, category: 'gate',   label: 'Front Gate',  notes: 'Padlocked — call (307) 555-0456' },
        { id: 5, lat: 43.070, lng: -107.280, category: 'hazard', label: 'Power Lines', notes: 'Low-clearance crossing over north field road' },
      ],
    };
    const base = this.getMockPropertyList().find(p => p.id === Number(id));
    if (!base) return null;
    return { ...base, features: features[base.id] ?? [] };
  }
}

export default new PropertiesService();

